import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { getProjects } from '../../lib/api';
import { cn } from '../../lib/utils';
import {
  FolderKanban,
  Calendar,
  User,
  Clock,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  Pause,
  Play,
  RefreshCw,
  Plus,
  Circle,
  CheckCircle,
  Loader2,
  Trash2,
  ListTodo,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  planning: { label: 'Planning', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', icon: Play },
  on_hold: { label: 'On Hold', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: Pause },
  completed: { label: 'Completed', color: 'bg-green-500/15 text-green-400 border-green-500/30', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/15 text-red-400 border-red-500/30', icon: AlertCircle },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
  medium: { label: 'Medium', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  high: { label: 'High', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  critical: { label: 'Critical', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

const TASK_STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Circle, color: 'text-muted-foreground' },
  in_progress: { label: 'In Progress', icon: Loader2, color: 'text-blue-400' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'text-green-400' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'Not set';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ProjectCard = ({ project, onTaskUpdate }) => {
  const [completing, setCompleting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  
  const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.planning;
  const priority = PRIORITY_CONFIG[project.priority] || PRIORITY_CONFIG.medium;
  const StatusIcon = status.icon;
  const isCompleted = project.status === 'completed';

  const tasks = project.tasks || [];
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const totalTasks = tasks.length;

  const handleCompleteProject = async (e) => {
    e.stopPropagation();
    if (isCompleted) return;
    if (!confirm(`Mark "${project.name}" as completed?`)) return;

    setCompleting(true);
    try {
      const response = await fetch(`${API_URL}/api/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed', progress_percent: 100 })
      });
      if (response.ok) onTaskUpdate?.();
    } catch (err) {
      console.error('Failed to complete project:', err);
    } finally {
      setCompleting(false);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    setAddingTask(true);
    try {
      const response = await fetch(`${API_URL}/api/projects/${project.id}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTaskTitle.trim() })
      });
      if (response.ok) {
        setNewTaskTitle('');
        onTaskUpdate?.();
      }
    } catch (err) {
      console.error('Failed to add task:', err);
    } finally {
      setAddingTask(false);
    }
  };

  const handleToggleTaskStatus = async (task) => {
    setUpdatingTaskId(task.id);
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      const response = await fetch(`${API_URL}/api/projects/${project.id}/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) onTaskUpdate?.();
    } catch (err) {
      console.error('Failed to update task:', err);
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${project.id}/tasks/${taskId}`, { method: 'DELETE' });
      if (response.ok) onTaskUpdate?.();
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  return (
    <Card
      className={cn(
        "bg-card/80 backdrop-blur-sm border-border/50 transition-colors hover:bg-card overflow-hidden",
        isCompleted && "opacity-60"
      )}
      data-testid={`project-card-${project.id}`}
    >
      {/* Compact header row - clickable to expand */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        data-testid={`project-row-toggle-${project.id}`}
      >
        {/* Chevron */}
        <span className="shrink-0 text-muted-foreground">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              "font-semibold text-sm truncate",
              isCompleted && "line-through"
            )}
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            {project.name}
          </h3>
        </div>

        {/* Priority */}
        <Badge variant="outline" className={cn("text-xs shrink-0", priority.color)} data-testid={`project-priority-${project.id}`}>
          {priority.label}
        </Badge>

        {/* Status */}
        <Badge variant="outline" className={cn("text-xs gap-1 shrink-0", status.color)} data-testid={`project-status-${project.id}`}>
          <StatusIcon className="w-3 h-3" />
          {status.label}
        </Badge>

        {/* Complete Button */}
        <Button
          size="sm"
          variant={isCompleted ? "ghost" : "outline"}
          className="h-7 px-2 shrink-0"
          onClick={handleCompleteProject}
          disabled={isCompleted || completing}
          data-testid={`project-complete-btn-${project.id}`}
        >
          {completing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              {isCompleted ? 'Done' : 'Complete'}
            </>
          )}
        </Button>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-4" data-testid={`project-details-${project.id}`}>
          {/* Description */}
          {project.description && (
            <p className="text-sm text-muted-foreground pt-3">{project.description}</p>
          )}

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{project.progress_percent || 0}%</span>
            </div>
            <Progress value={project.progress_percent || 0} className="h-1.5" />
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
            {project.project_lead && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {project.project_lead}
              </span>
            )}
            {project.target_completion && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Due {formatDate(project.target_completion)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <ListTodo className="w-3 h-3" />
              {totalTasks > 0 ? `${completedTasks}/${totalTasks} tasks` : 'No tasks'}
            </span>
          </div>

          {/* Tasks */}
          <div className="border-t border-border/30 pt-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tasks</p>
            {tasks.length > 0 && (
              <div className="space-y-1.5">
                {tasks.map((task) => {
                  const taskStatus = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.pending;
                  const TaskIcon = updatingTaskId === task.id ? Loader2 : taskStatus.icon;
                  const taskCompleted = task.status === 'completed';
                  return (
                    <div
                      key={task.id}
                      className={cn(
                        "flex items-start gap-2 p-2 rounded-lg transition-colors group",
                        taskCompleted ? "bg-green-500/5" : "hover:bg-muted/50"
                      )}
                      data-testid={`task-${task.id}`}
                    >
                      <button
                        onClick={() => handleToggleTaskStatus(task)}
                        disabled={updatingTaskId === task.id}
                        className={cn(
                          "mt-0.5 shrink-0 transition-colors",
                          taskStatus.color,
                          updatingTaskId === task.id && "animate-spin"
                        )}
                        data-testid={`task-toggle-${task.id}`}
                      >
                        <TaskIcon className="w-4 h-4" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm", taskCompleted && "line-through text-muted-foreground")}>{task.title}</p>
                        {task.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteTask(task.id)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                        data-testid={`task-delete-${task.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add task */}
            <form onSubmit={handleAddTask} className="flex gap-2 mt-2">
              <Input
                type="text"
                placeholder="Add a new task..."
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                className="h-8 text-sm"
                disabled={addingTask}
                data-testid={`task-input-${project.id}`}
              />
              <Button
                type="submit"
                size="sm"
                className="h-8 px-2"
                disabled={addingTask || !newTaskTitle.trim()}
                data-testid={`task-add-btn-${project.id}`}
              >
                {addingTask ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </form>
          </div>

          {/* Status Updates */}
          {project.status_updates?.length > 0 && (
            <div className="border-t border-border/30 pt-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Recent Updates
              </p>
              <div className="space-y-3">
                {project.status_updates.slice(0, 3).map((update) => (
                  <div key={update.id} className="flex gap-3 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground">{update.content}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {update.created_by && <span>{update.created_by} &middot; </span>}
                        {formatDate(update.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export const ProjectsSection = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [sortBy, setSortBy] = useState('default'); // default | status | priority | name

  const fetchProjects = async () => {
    try {
      const res = await getProjects();
      setProjects(res.data?.projects || []);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <RefreshCw className="w-5 h-5 animate-spin mr-3" />
        <span>Loading projects...</span>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <Card className="bg-card/80 backdrop-blur-sm border-border/50 p-8 text-center" data-testid="no-projects">
        <FolderKanban className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
        <p className="text-muted-foreground">No projects yet</p>
        <p className="text-sm text-muted-foreground mt-1">Projects will appear here once created by a project manager</p>
      </Card>
    );
  }

  // Sorting helpers
  const STATUS_ORDER = { in_progress: 0, planning: 1, on_hold: 2, cancelled: 3, completed: 4 };
  const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
  const sortProjects = (list) => {
    const arr = [...list];
    if (sortBy === 'status') {
      arr.sort((a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99));
    } else if (sortBy === 'priority') {
      arr.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99));
    } else if (sortBy === 'name') {
      arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return arr;
  };

  // Split projects: active first, completed at bottom
  const activeProjects = sortProjects(projects.filter(p => p.status !== 'completed'));
  const completedProjects = sortProjects(projects.filter(p => p.status === 'completed'));

  // Summary stats
  const activeCount = projects.filter(p => p.status === 'in_progress').length;
  const completedCount = completedProjects.length;

  return (
    <div className="space-y-4" data-testid="projects-section-content">
      {/* Summary Row */}
      <div className="grid grid-cols-3 gap-4 mb-2">
        <div className="rounded-lg border border-border/50 bg-card/60 p-3 text-center">
          <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>{projects.length}</p>
          <p className="text-xs text-muted-foreground">Total Projects</p>
        </div>
        <div className="rounded-lg border border-border/50 bg-card/60 p-3 text-center">
          <p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: 'Chivo, sans-serif' }}>{activeCount}</p>
          <p className="text-xs text-muted-foreground">Active</p>
        </div>
        <div className="rounded-lg border border-border/50 bg-card/60 p-3 text-center">
          <p className="text-2xl font-bold text-muted-foreground" style={{ fontFamily: 'Chivo, sans-serif' }}>{completedCount}</p>
          <p className="text-xs text-muted-foreground">Completed</p>
        </div>
      </div>

      {/* Sort Controls */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <label htmlFor="project-sort" className="text-xs text-muted-foreground">Sort by:</label>
        <select
          id="project-sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="text-xs bg-card border border-border/50 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="project-sort-select"
        >
          <option value="default">Default</option>
          <option value="status">Status</option>
          <option value="priority">Priority</option>
          <option value="name">Name (A–Z)</option>
        </select>
      </div>

      {/* Active Project Cards - single line each */}
      {activeProjects.length > 0 && (
        <div className="space-y-2">
          {activeProjects.map((project) => (
            <ProjectCard key={project.id} project={project} onTaskUpdate={fetchProjects} />
          ))}
        </div>
      )}

      {/* Completed Projects Toggle */}
      {completedCount > 0 && (
        <div className="pt-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="show-completed-toggle">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="rounded border-border accent-primary w-4 h-4"
            />
            Show completed projects ({completedCount})
          </label>

          {showCompleted && (
            <div className="space-y-2 mt-3">
              {completedProjects.map((project) => (
                <ProjectCard key={project.id} project={project} onTaskUpdate={fetchProjects} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeProjects.length === 0 && !showCompleted && (
        <Card className="bg-card/80 backdrop-blur-sm border-border/50 p-6 text-center">
          <p className="text-muted-foreground">All projects are completed!</p>
        </Card>
      )}
    </div>
  );
};
